import { useRef, useState, useEffect, useCallback } from "react";
import { Card, Typography, Table } from "antd";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import api from '../interceptors/api';
import { toast } from "react-hot-toast";
import StandardButton from "../components/StandardButton";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { pageUrl } from "../utils/paths";

// Keep client-side cap below the backend cap so users hit the friendly
// browser-side stop before the server rejects the upload.
const MAX_RECORDING_SECONDS = 30 * 60;
const MAX_RECORDING_BYTES = 700 * 1024 * 1024;

const formatElapsed = (seconds) => {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

const PublicLanding = ({ t }) => {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  const supportedLangs = ["en", "es", "zh-CN", "de", "fr", "ja"];
  const currentLang = supportedLangs.includes(segments[0]) ? segments[0] : 'en';
  const loginHref = `/${currentLang}/auth/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
  const title = t("screen_recorder_name") || "Online Screen Recorder";
  const description = t("screen_recorder_desc")
    || "Record your screen directly in the browser — no software installation needed.";

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-5xl mx-auto p-6">
        <Helmet>
          <title>{`${title} — Quto.dev`}</title>
          <meta name="description" content={description} />
          <link rel="canonical" href={pageUrl(currentLang, "/tools/screen-recorder")} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content="https://quto.dev/splash_screen_recording.png" />
        </Helmet>
        <Card className="!p-0 overflow-hidden">
          <div className="grid md:grid-cols-2 gap-6 p-6">
            <div>
              <Typography.Title level={2} className="!mb-3">{title}</Typography.Title>
              <Typography.Paragraph className="!mb-4 text-base">
                {description}
              </Typography.Paragraph>
              <ul className="space-y-2 text-sm text-gray-700 list-disc pl-5 mb-6">
                <li>{t("screen_recorder_feature_unlimited") || "Unlimited recordings, no watermark, no time limit per video up to 30 minutes."}</li>
                <li>{t("screen_recorder_feature_audio") || "System audio plus microphone, mixed in the browser."}</li>
                <li>{t("screen_recorder_feature_share") || "One-click share links to send to teammates or customers."}</li>
                <li>{t("screen_recorder_feature_private") || "Your video stays private — only people with the share link can see it."}</li>
              </ul>
              <StandardButton type="primary" size="large" href={loginHref}>
                {t("screen_recorder_landing_cta") || "Sign in to start recording"}
              </StandardButton>
            </div>
            <div className="flex items-center justify-center">
              <img
                src="/splash_screen_recording.png"
                alt={title}
                className="w-full max-w-sm rounded-lg"
              />
            </div>
          </div>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

const ScreenRecorder = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isAuthenticated = !!localStorage.getItem('authToken');

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const isUploadingRef = useRef(false);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(0);
  const recordingDurationRef = useRef(0);
  const recordingDimensionsRef = useRef({ width: null, height: null });
  const timerRef = useRef(null);
  const stoppedByCapRef = useRef(false);

  const [starting, setStarting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadRecordings = useCallback(async () => {
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) return;
      setLoading(true);
      const res = await api.get("/screen-record/list");
      setRecordings(res.data.recordings || []);
    } catch (err) {
      toast.error(t("failed_to_load_recordings"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (isAuthenticated) loadRecordings();
  }, [isAuthenticated, loadRecordings]);

  const stopAllTracks = (stream) => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        try { track.stop(); } catch (_) {}
        track.enabled = false;
      });
    }
  };

  const closeAudioContext = async () => {
    if (audioContextRef.current) {
      try { await audioContextRef.current.close(); } catch (_) {}
      audioContextRef.current = null;
    }
  };

  const teardownStreams = async () => {
    if (displayStreamRef.current) {
      stopAllTracks(displayStreamRef.current);
      displayStreamRef.current = null;
    }
    if (micStreamRef.current) {
      stopAllTracks(micStreamRef.current);
      micStreamRef.current = null;
    }
    if (mediaStreamRef.current) {
      stopAllTracks(mediaStreamRef.current);
      mediaStreamRef.current = null;
    }
    await closeAudioContext();
  };

  const saveRecordingToDB = async (recordingData) => {
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) return null;
      const res = await api.post("/screen-record/save", recordingData);
      return res?.data || null;
    } catch (_) {
      return null;
    }
  };

  const uploadToS3 = async (blob, duration) => {
    const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
    if (!userProfileData?.id) return null;

    const toastId = toast.loading(t("uploading_text"));
    try {
      const signRes = await api.post("/screen-record/presign", {});
      const { uploadUrl, downloadUrl, key } = signRes.data;
      if (!uploadUrl) throw new Error(t("failed_to_get_upload_url"));

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "video/webm" },
        body: blob,
      });
      if (!putRes.ok) throw new Error(t("failed_to_upload_to_s3"));

      const { width, height } = recordingDimensionsRef.current;
      const recordingData = {
        key,
        url: downloadUrl,
        filename: t("recording_filename_prefix") + Date.now() + ".webm",
        size: blob.size,
        duration,
        width,
        height,
      };

      await saveRecordingToDB(recordingData);
      await api.post("/screen-record/complete", {
        key,
        size: blob.size,
        duration,
      });

      toast.success(t("uploading_success"), { id: toastId });
      await loadRecordings();
      return { url: downloadUrl, key, size: blob.size, duration };
    } catch (err) {
      toast.error(`${t("uploading_error")} ${err.message}`, { id: toastId });
      throw err;
    }
  };

  const finalizeUpload = async (blob, duration) => {
    if (isUploadingRef.current) return;
    isUploadingRef.current = true;
    setUploading(true);
    try {
      await uploadToS3(blob, duration);
      setPendingBlob(null);
    } catch (_) {
      setPendingBlob({ blob, duration });
    } finally {
      isUploadingRef.current = false;
      setUploading(false);
    }
  };

  const handleRetryUpload = async () => {
    if (!pendingBlob) return;
    await finalizeUpload(pendingBlob.blob, pendingBlob.duration);
  };

  const handleDiscardPending = () => {
    setPendingBlob(null);
    toast.success(t("recording_discarded") || "Recording discarded");
  };

  const stopRecording = useCallback(() => {
    if (!mediaRecorderRef.current) {
      setRecording(false);
      return;
    }
    if (mediaRecorderRef.current.state === "recording") {
      try {
        mediaRecorderRef.current.stop();
        toast.success(t("recording_stopped_uploading"));
        setRecording(false);
      } catch (err) {
        toast.error(t("error_stopping_recording") || "Error stopping recording");
        setRecording(false);
        teardownStreams();
      }
    } else {
      setRecording(false);
    }
  }, [t]);

  const startRecording = async () => {
    if (starting || recording || uploading) {
      toast.error(t("recording_already_in_progress") || "Recording already in progress");
      return;
    }

    setStarting(true);
    setElapsed(0);
    stoppedByCapRef.current = false;
    chunksRef.current = [];

    let displayStream;
    let micStream;
    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      displayStreamRef.current = displayStream;

      const settings = displayStream.getVideoTracks()[0]?.getSettings?.() || {};
      recordingDimensionsRef.current = {
        width: settings.width || null,
        height: settings.height || null,
      };

      let hasMic = false;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: false,
        });
        micStreamRef.current = micStream;
        hasMic = true;
      } catch (_) {
        toast(t("microphone_permission_denied"));
      }

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      if (audioContext.state === "suspended") await audioContext.resume();

      const mixedAudioDestination = audioContext.createMediaStreamDestination();
      let hasSystemAudio = false;

      if (displayStream.getAudioTracks().length > 0) {
        try {
          audioContext.createMediaStreamSource(displayStream).connect(mixedAudioDestination);
          hasSystemAudio = true;
        } catch (_) {}
      }
      if (hasMic && micStream.getAudioTracks().length > 0) {
        try {
          audioContext.createMediaStreamSource(micStream).connect(mixedAudioDestination);
        } catch (_) {}
      }

      const audioTracks = mixedAudioDestination.stream.getAudioTracks();
      const mixedStream = new MediaStream([
        ...displayStream.getVideoTracks(),
        ...audioTracks,
      ]);
      mediaStreamRef.current = mixedStream;

      if (!hasSystemAudio && !hasMic) {
        toast(t("recording_video_only") || "No audio sources available. Recording video only.");
      }

      const mimeTypes = [
        "video/mp4;codecs=avc1,mp4a.40.2",
        "video/mp4",
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=h264,opus",
        "video/webm",
      ];
      let selectedMimeType = "";
      for (const mt of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mt)) { selectedMimeType = mt; break; }
      }

      const recorder = new MediaRecorder(mixedStream, {
        mimeType: selectedMimeType,
        videoBitsPerSecond: 2500000,
        audioBitsPerSecond: 128000,
      });

      let totalSize = 0;
      startTimeRef.current = Date.now();

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          totalSize += e.data.size;
          if (totalSize > MAX_RECORDING_BYTES && recorder.state === "recording") {
            stoppedByCapRef.current = true;
            toast.error(t("recording_size_cap_hit") || "Recording reached the size limit. Stopping.");
            try { recorder.stop(); } catch (_) {}
          }
        }
      };

      recorder.onstop = async () => {
        recordingDurationRef.current = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const blob = new Blob(chunksRef.current, { type: selectedMimeType || "video/webm" });
        chunksRef.current = [];
        await teardownStreams();
        mediaRecorderRef.current = null;
        await finalizeUpload(blob, recordingDurationRef.current);
      };

      const videoTrack = displayStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          if (recorder.state === "recording") stopRecording();
        };
      }

      mediaRecorderRef.current = recorder;
      recorder.start(1000);
      // Flip to "recording" only after the recorder has actually started, so
      // the elapsed-timer effect doesn't fire while the screen picker is
      // still open (which would briefly compute an enormous "elapsed" value
      // and trigger the duration cap).
      setStarting(false);
      setRecording(true);
    } catch (err) {
      await teardownStreams();
      setStarting(false);
      setRecording(false);
      if (err?.name === "NotAllowedError") {
        toast.error(t("permission_denied_screen_share"));
      } else if (err?.name === "NotFoundError") {
        toast.error(t("no_screen_available"));
      } else if (err?.name === "NotSupportedError") {
        toast.error(t("audio_mixing_not_supported") || "Audio mixing not supported in this browser");
      } else {
        toast.error(`${t("screen_recording_error")}: ${err?.message || err}`);
      }
    }
  };

  // Live elapsed timer + hard duration cap.
  useEffect(() => {
    if (!recording) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }
    timerRef.current = setInterval(() => {
      const seconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsed(seconds);
      if (seconds >= MAX_RECORDING_SECONDS && !stoppedByCapRef.current) {
        stoppedByCapRef.current = true;
        toast.error(t("recording_duration_cap_hit") || "Reached 30 minute limit. Stopping.");
        stopRecording();
      }
    }, 250);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [recording, stopRecording, t]);

  // Warn (but don't try to stop async) on close while recording.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (recording || pendingBlob) {
        e.preventDefault();
        e.returnValue = t("recording_in_progress_warning");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [recording, pendingBlob, t]);

  // Clean up if the component unmounts mid-recording.
  useEffect(() => () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
    teardownStreams();
  }, []);

  const handleCopyUrl = (record) => {
    const slug = record?.share_slug;
    const target = slug ? `${window.location.origin}/r/${slug}` : record?.url;
    if (!target) {
      toast.error(t("failed_to_load_recordings"));
      return;
    }
    navigator.clipboard.writeText(target);
    toast.success(t("url_copied_to_clipboard"));
  };

  const handleDownload = async (url, filename) => {
    try {
      toast.loading(t("downloading") || "Downloading...", { id: "download" });
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = (filename || "recording.webm").replace(/\.webm$/, ".mp4");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      toast.success(t("download_complete") || "Download complete", { id: "download" });
    } catch (_) {
      toast.error(t("download_failed") || "Download failed", { id: "download" });
    }
  };

  const handleDeleteRecording = async (id) => {
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) return;
      await api.delete(`/screen-record/${id}`);
      toast.success(t("recording_deleted_successfully"));
      await loadRecordings();
    } catch (_) {
      toast.error(t("failed_to_delete_recording"));
    }
  };

  const columns = [
    {
      title: t("preview"),
      key: "preview",
      width: 200,
      render: (_, record) => (
        <video src={record.url} controls className="w-full h-32 object-cover rounded" />
      ),
    },
    {
      title: t("filename"),
      dataIndex: "filename",
      key: "filename",
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: t("size"),
      dataIndex: "size_bytes",
      key: "size_bytes",
      width: 120,
      render: (size) => `${(size / (1024 * 1024)).toFixed(2)} MB`,
    },
    {
      title: t("duration"),
      dataIndex: "recording_duration",
      key: "duration",
      width: 120,
      render: (d) => formatElapsed(d),
    },
    {
      title: t("date"),
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: t("actions"),
      key: "actions",
      width: 360,
      render: (_, record) => {
        const shareUrl = record.share_slug
          ? `${window.location.origin}/r/${record.share_slug}`
          : record.url;
        return (
          <div className="flex flex-wrap gap-2">
            <StandardButton type="secondary" size="small" onClick={() => handleCopyUrl(record)}>
              {t("copy_url_button") || "Copy URL"}
            </StandardButton>
            <StandardButton type="secondary" size="small" href={shareUrl} target="_blank" rel="noopener noreferrer">
              {t("open_button") || "Open"}
            </StandardButton>
            <StandardButton type="secondary" size="small" onClick={() => handleDownload(record.url, record.filename)}>
              {t("download_button") || "Download"}
            </StandardButton>
            <StandardButton type="secondary" size="small" danger onClick={() => handleDeleteRecording(record.id)}>
              {t("delete_button") || "Delete"}
            </StandardButton>
          </div>
        );
      },
    },
  ];

  if (!isAuthenticated) {
    if (location.pathname.startsWith("/tools/screen-recorder")
        || !/^\/(en|es|zh-CN|de|fr|ja)\b/.test(location.pathname)) {
      return <PublicLanding t={t} />;
    }
    return <PublicLanding t={t} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-5xl mx-auto p-6">
        <Card className="space-y-6">
          <div className="text-center">
            <StandardButton
              onClick={starting ? undefined : recording ? stopRecording : startRecording}
              disabled={starting || uploading}
              loading={starting}
              type="secondary"
              danger={recording}
              size="large"
              className="px-8"
            >
              {starting
                ? t("requesting_screen") || "Requesting screen…"
                : recording
                ? t("stop_recording_button")
                : t("start_recording_button")}
            </StandardButton>
          </div>

          {recording && (
            <div className="text-center mt-4">
              <Typography.Text strong className="text-lg">
                {formatElapsed(elapsed)} / {formatElapsed(MAX_RECORDING_SECONDS)}
              </Typography.Text>
              <div>
                <Typography.Text type="secondary">
                  {t("recording_in_progress")}
                </Typography.Text>
              </div>
            </div>
          )}

          {uploading && (
            <div className="text-center mt-4">
              <Typography.Text type="secondary">
                {t("uploading_recording") || "Uploading recording..."}
              </Typography.Text>
            </div>
          )}

          {pendingBlob && !uploading && (
            <div className="text-center mt-4 p-4 border border-red-200 bg-red-50 rounded">
              <Typography.Text strong className="block mb-2">
                {t("upload_failed_retry_prompt") || "Upload failed. Your recording is still in memory."}
              </Typography.Text>
              <div className="flex gap-2 justify-center">
                <StandardButton type="primary" size="middle" onClick={handleRetryUpload}>
                  {t("retry_upload_button") || "Retry upload"}
                </StandardButton>
                <StandardButton type="secondary" size="middle" danger onClick={handleDiscardPending}>
                  {t("discard_button") || "Discard"}
                </StandardButton>
              </div>
            </div>
          )}
        </Card>

        {recordings.length > 0 && (
          <Card className="mt-8">
            <Typography.Title level={4}>{t("all_recordings_title")}</Typography.Title>
            <Table
              rowKey="id"
              dataSource={recordings}
              columns={columns}
              loading={loading}
              pagination={{ pageSize: 10 }}
              className="mt-4"
            />
          </Card>
        )}

        {recordings.length === 0 && !loading && (
          <Card className="mt-8">
            <div className="text-center py-8">
              <Typography.Text type="secondary">{t("no_recordings_yet")}</Typography.Text>
            </div>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ScreenRecorder;
